<script setup lang="ts">
import { computed } from 'vue';
import { useCustomerConfig } from '@peanuds/app-shell';

import type { AppNotification } from '../types/notifications.types';
import { formatDateTime } from '../utils/formatDateTime';
import NotificationRowMessageContent from './NotificationRowMessageContent.vue';

interface Props {
  notification: AppNotification;
}

interface Emits {
  markAsRead: [id: string];
}

const { notification } = defineProps<Props>();
const emit = defineEmits<Emits>();

const { dateTime } = useCustomerConfig();

const formattedTime = computed(() => formatDateTime(notification.createdAt, dateTime.value));
const isUnread = computed(() => !notification.read);

const handleRowClick = () => {
  if (notification.read) {
    return;
  }

  emit('markAsRead', notification.id);
};
</script>

<template>
  <div
    class="notification-row"
    :class="{ 'notification-row--unread': isUnread, 'notification-row--read': !isUnread }"
    role="listitem"
    tabindex="0"
    @click="handleRowClick"
    @keydown.enter.prevent="handleRowClick"
    @keydown.space.prevent="handleRowClick"
  >
    <div class="notification-row__content">
      <div class="notification-row__text">
        <NotificationRowMessageContent
          :notification="notification"
          :formatted-time="formattedTime"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.notification-row {
  background: var(--p-surface-50);
  border-bottom: 1px solid var(--p-surface-200);
  display: flex;
  padding: $static-space-4 $static-space-4 $static-space-4 $static-space-6;
  cursor: pointer;
  transition: background-color 120ms ease;

  &--unread {
    background: var(--p-surface-50);
  }

  &--read {
    background: var(--p-surface-0);
  }

  &:hover {
    background: var(--p-surface-100);
  }

  &--read:hover {
    background: var(--p-surface-50);
  }

  &:focus-visible {
    outline: 2px solid var(--p-primary-500);
    outline-offset: -2px;
  }
}

.notification-row__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  padding: $static-space-1 0;
}

.notification-row__text {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;

  :deep(.notification-message__title) {
    margin: 0;
    width: 100%;
    font-size: $text-lg;
    font-weight: $font-semibold;
    line-height: 1.4;
    color: var(--p-surface-700);
  }

  :deep(.notification-message__date) {
    margin: $static-space-1 0 0;
    width: 100%;
    font-size: $text-xs;
    font-weight: $font-regular;
    font-style: italic;
    line-height: 1.4;
    color: var(--p-surface-500);
  }

  :deep(.notification-message__description) {
    margin: $static-space-2 0 0;
    width: 100%;
    white-space: pre-line;
    font-size: $text-md;
    font-weight: $font-regular;
    line-height: 1.5;
    color: var(--p-surface-600);
  }
}
</style>
